import {
  Box,
  CircularProgress,
  Collapse,
  Grid,
  TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Utils from '../../commons/utils/Utils.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';

const FILTER_STATE_INIT = {
  dominio: '',
  modelo: '',
  tipo: null,
  base: [],
  region: [],
  subRegion: null,
  centroCostos: null,
  motivo: null,
  transportadora: null,
};

const buildQuery = (state) => ({
  dominio:      state.dominio || '',
  modelo:       state.modelo || '',
  tipo:         state.tipo        ? state.tipo.label        : '',
  base:         state.base        ? state.base.map(e => e.value).join(',') : '',
  region:       state.region      ? state.region.map(e => e.value).join(',') : '',
  subRegion:    state.subRegion   ? state.subRegion.value   : '',
  centroCostos: state.centroCostos ? state.centroCostos.value : '',
  motivo:       state.motivo      ? state.motivo.value      : '',
  transportadora: state.transportadora ? state.transportadora.nombre : '',
});

const DisponibilidadActivaGridFilters = ({ dropdownActive, loading, onSearch, onReset }) => {
  const intl = useIntl();
  const [filtersState, setFiltersState] = useState({ ...FILTER_STATE_INIT });
  const [loadingOptions, setLoadingOptions] = useState({
    tipos: true,
    motivos: true,
    regiones: true,
    bases: true,
    subRegiones: true,
    centrosCostos: true,
  });
  const [tipos, setTipos] = useState([]);
  const [motivos, setMotivos] = useState([]);
  const [regiones, setRegiones] = useState([]);
  const [bases, setBases] = useState([]);
  const [subRegiones, setSubRegiones] = useState([]);
  const [centrosCostos, setCentrosCostos] = useState([]);
  const [transportadoras, setTransportadoras] = useState([]);
  const transportadoraEnabled = ConfigBusiness.get('moviles.transportadora.grilla') === 'true';

  useEffect(() => {
    Utils.getData('/modelo-tipos/select')
      .then(data => setTipos(data || []))
      .catch(() => {})
      .finally(() => setLoadingOptions(prev => ({ ...prev, tipos: false })));

    Utils.getData('/motivos/ociosidad/select')
      .then(data => {
        const updatedMotivos = [
          { value: 'null', label: intl.formatMessage({ id: 'disponibilidadGridRow_sin_motivo', defaultMessage: 'Sin motivo' }) },
          ...(data || []),
        ];
        setMotivos(updatedMotivos);
      })
      .catch(() => {})
      .finally(() => setLoadingOptions(prev => ({ ...prev, motivos: false })));

    Utils.getData('/regiones/select')
      .then(data => setRegiones(data || []))
      .catch(() => {})
      .finally(() => setLoadingOptions(prev => ({ ...prev, regiones: false })));

    Utils.getData('/bases/select-filtered-by-user/movil')
      .then(data => setBases(data || []))
      .catch(() => {})
      .finally(() => setLoadingOptions(prev => ({ ...prev, bases: false })));

    Utils.getData('/subregiones/select-all')
      .then(data => setSubRegiones(data || []))
      .catch(() => {})
      .finally(() => setLoadingOptions(prev => ({ ...prev, subRegiones: false })));

    Utils.getData('/centros-costos/select')
      .then(data => setCentrosCostos(data || []))
      .catch(() => {})
      .finally(() => setLoadingOptions(prev => ({ ...prev, centrosCostos: false })));

    if (transportadoraEnabled) {
      Utils.getData('/transportadoras/select')
        .then(data => setTransportadoras(data || []))
        .catch(() => {});
    }
  }, []);

  const handleChange = (name, value) => {
    setFiltersState(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e && e.preventDefault();
    onSearch(buildQuery(filtersState));
  };

  const handleReset = () => {
    const clean = { ...FILTER_STATE_INIT };
    setFiltersState(clean);
    onReset();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  const autocomplete = (name, options, isLoading, label, config = {}) => {
    const { multiple = false, getOptionLabel = (option) => option ? option.label : '' } = config;
    return (
      <Autocomplete
        size={multiple ? 'medium' : 'small'}
        multiple={multiple}
        name={name}
        disabled={isLoading}
        options={options}
        value={filtersState[name]}
        defaultValue={multiple ? [] : null}
        getOptionLabel={getOptionLabel}
        onChange={(e, value) => handleChange(name, value)}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            label={label}
            placeholder={label}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="inherit" size={21} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    );
  };

  const textField = (name, label) => (
    <TextField
      label={label}
      placeholder={label}
      name={name}
      value={filtersState[name]}
      onChange={(e) => handleChange(name, e.target.value)}
      onKeyDown={handleKeyDown}
      disabled={loading}
      className="col-12"
    />
  );

  return (
    <Collapse in={dropdownActive}>
      <Grid container item xs={12} spacing={3} style={{ marginBottom: 20 }}>
        <Grid component={Box} item xs={3}>
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            {textField('dominio', intl.formatMessage({ id: 'disponibilidadActiva.filtros.dominio', defaultMessage: 'Dominio' }))}
          </form>
        </Grid>
        <Grid component={Box} item xs={3}>
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            {textField('modelo', intl.formatMessage({ id: 'disponibilidadActiva.filtros.modelo', defaultMessage: 'Modelo' }))}
          </form>
        </Grid>
        <Grid component={Box} item xs={3}>
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            {autocomplete(
              'tipo',
              tipos,
              loadingOptions.tipos,
              intl.formatMessage({ id: 'disponibilidadActiva.filtros.tipo', defaultMessage: 'Tipo' })
            )}
          </form>
        </Grid>
        <Grid component={Box} item xs={3}>
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            {autocomplete(
              'region',
              regiones,
              loadingOptions.regiones,
              intl.formatMessage({ id: 'disponibilidadActiva.filtros.region', defaultMessage: 'Región' }),
              { multiple: true }
            )}
          </form>
        </Grid>
        <Grid component={Box} item xs={3}>
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            {autocomplete(
              'subRegion',
              subRegiones,
              loadingOptions.subRegiones,
              intl.formatMessage({ id: 'disponibilidadActiva.filtros.subRegion', defaultMessage: 'Sub.Región' })
            )}
          </form>
        </Grid>
        <Grid component={Box} item xs={3}>
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            {autocomplete(
              'base',
              bases,
              loadingOptions.bases,
              intl.formatMessage({ id: 'disponibilidadActiva.filtros.base', defaultMessage: 'Base' }),
              { multiple: true }
            )}
          </form>
        </Grid>
        <Grid component={Box} item xs={3}>
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            {autocomplete(
              'centroCostos',
              centrosCostos,
              loadingOptions.centrosCostos,
              intl.formatMessage({ id: 'disponibilidadActiva.filtros.centroCostos', defaultMessage: 'Centro de Costos' })
            )}
          </form>
        </Grid>
        <Grid component={Box} item xs={3}>
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            {autocomplete(
              'motivo',
              motivos,
              loadingOptions.motivos,
              intl.formatMessage({ id: 'disponibilidadActiva.filtros.motivo', defaultMessage: 'Motivo' })
            )}
          </form>
        </Grid>
        {transportadoraEnabled && (
          <Grid component={Box} item xs={3}>
            <form noValidate autoComplete="off" onSubmit={handleSubmit}>
              {autocomplete(
                'transportadora',
                transportadoras,
                false,
                intl.formatMessage({ id: 'disponibilidadActiva.filtros.transportadora', defaultMessage: 'Transportadora' }),
                { getOptionLabel: (option) => option ? option.nombre : '' }
              )}
            </form>
          </Grid>
        )}
        <Grid
          xs={12}
          container
          justifyContent="flex-end"
          alignItems="center"
        >
          <Grid component={Box} item className="align-self-end mr-1">
            <button
              disabled={loading}
              className="btn btn-primary mr-1"
              onClick={handleSubmit}
            >
              <i className="fa fa-search fa-xs" />{'  '}
              <FormattedMessage id="Buscar" defaultMessage="Buscar" />
            </button>
          </Grid>
          <Grid component={Box} item className="align-self-end">
            <button
              disabled={loading}
              className="btn btn-primary mr-1"
              onClick={handleReset}
            >
              <i className="fa fa-clean fa-xs" />{'  '}
              <FormattedMessage id="Limpiar" defaultMessage="Limpiar" />
            </button>
          </Grid>
        </Grid>
      </Grid>
    </Collapse>
  );
};

export default DisponibilidadActivaGridFilters;
